import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, Bookmark } from "lucide-react";
import "./resultsearch.css";
import PremiumModal from "../../../modal/premiummodal";
import Pagination from "../pagination";
const Resultsearch = ({ data, searchQuery, onPageChange }) => {
    const [activeTab, setActiveTab] = useState({});
    const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
    const [searchData, setSearchData] = useState(data || { results: [], count: 0 });
    useEffect(() => {
        if (data) {
            setSearchData(data);
        }
    }, [data]);

    const resultdata = searchData?.results || [];

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    };

    const createContextualExcerpt = (fullText, keywords) => {
        if (!fullText) return "";

        let allKeywords = [...(keywords || [])];
        if (searchQuery) {
            const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
            searchTerms.forEach(term => {
                if (!allKeywords.includes(term)) {
                    allKeywords.push(term);
                }
            });
        }

        if (allKeywords.length === 0) {
            return fullText.substring(0, 300) + "...";
        }

        const excerptSegments = [];
        const maxExcerptLength = 300;
        let totalLength = 0;

        const keywordRegex = new RegExp(`(${allKeywords.join('|')})`, 'gi');

        let match;
        const matches = [];
        while ((match = keywordRegex.exec(fullText)) !== null) {
            matches.push({
                keyword: match[0],
                index: match.index
            });
        }

        if (matches.length === 0) {
            return fullText.substring(0, maxExcerptLength) + "...";
        }

        matches.forEach((match, index) => {
            if (totalLength >= maxExcerptLength) return;

            const contextSize = 30;
            const start = Math.max(0, match.index - contextSize);
            const end = Math.min(fullText.length, match.index + match.keyword.length + contextSize);

            let snippet = fullText.substring(start, end);

            if (start > 0) snippet = "..." + snippet;
            if (end < fullText.length) snippet = snippet + "...";

            if (totalLength + snippet.length <= maxExcerptLength || excerptSegments.length === 0) {
                excerptSegments.push(snippet);
                totalLength += snippet.length;
            }
        });
        return excerptSegments.join(" ");
    };

    const highlightKeywords = (text, keywords) => {
        if (!text) return "";
        if (!keywords || keywords.length === 0) return text;

        let termsToHighlight = [...keywords];
        if (searchQuery) {
            const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
            searchTerms.forEach(term => {
                if (!termsToHighlight.includes(term)) {
                    termsToHighlight.push(term);
                }
            });
        }

        if (termsToHighlight.length === 0) return text;

        const escapedTerms = termsToHighlight.map(term =>
            term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

        return text.replace(regex, (match) => `<mark>${match}</mark>`);
    };

    const handleTabChange = (resultId, tab) => {
        setActiveTab(prev => ({
            ...prev,
            [resultId]: tab
        }));
    };

    const handleBookmarkClick = () => {
        setIsPremiumModalOpen(true);
    };

    const handleSubscribe = () => {
        setIsPremiumModalOpen(false);
        console.log("Navigate to subscription page");
    };

    const handlePageChange = (page) => {
        if (onPageChange) {
            onPageChange(page);
        }
    };

    return (
        <div className="result-list">
            <PremiumModal
                isOpen={isPremiumModalOpen}
                onClose={() => setIsPremiumModalOpen(false)}
                onSubscribe={handleSubscribe}
            />
            <div className="result-count">
                <h2>{searchData.count} Résultat{searchData.count > 1 ? 's' : ''} trouvé{searchData.count > 1 ? 's' : ''}</h2>
                <div className="sort-options">
                    <span>Trier par</span>
                    <select className="sort-select">
                        <option value="pertinence">Pertinence</option>
                        <option value="date">Date</option>
                        <option value="chambre">Chambre</option>
                    </select>
                </div>
            </div>

            <div className="results">
                {resultdata.map((result, index) => {
                    const allKeywords = [...(result.mots_cles || ['Servitude', 'Pourvoi', 'Propriété', 'Donation'])];
                    if (searchQuery) {
                        const searchTerms = searchQuery.toLowerCase().split(/\s+/).filter(term => term.length > 2);
                        searchTerms.forEach(term => {
                            if (!allKeywords.includes(term)) {
                                allKeywords.push(term);
                            }
                        });
                    }
                    return (
                        <div key={result.id} className="result-item">
                            <div className="result-header">
                                <h3 className="result-title">
                                    <Link to={`/decision/${result.id}`} className="result-title-link">
                                        {index + 1}. {result?.juridiction}, {result?.type_decision || result?.chambre}, du {formatDate(result?.date_emission)}, {result.reference}
                                    </Link>
                                </h3>
                                <div className="portee-badge">
                                    {result.portee === "majeure" && <span className="portee-majeure">Portée majeure</span>}
                                </div>
                                <button className="bookmark-button" onClick={handleBookmarkClick}>
                                    <Bookmark size={20} />
                                </button>

                                <div className="result-status">
                                    {result.status && <span className="status-label">{result.status}</span>}
                                </div>

                                <div className="result-links-top">
                                    <button
                                        className={`result-link ${(!activeTab[result.id] || activeTab[result.id] === 'extrait') ? 'active' : ''}`}
                                        onClick={() => handleTabChange(result.id, 'extrait')}
                                        style={{
                                            borderBottom: (!activeTab[result.id] || activeTab[result.id] === 'extrait') ? '2px solid #F57C00' : 'none'
                                        }}
                                    >
                                        Extrait
                                    </button>
                                    <button
                                        className={`result-summary-link ${activeTab[result.id] === 'resume' ? 'active' : ''}`}
                                        onClick={() => handleTabChange(result.id, 'resume')}
                                        style={{
                                            borderBottom: activeTab[result.id] === 'resume' ? '2px solid #F57C00' : 'none'
                                        }}
                                    >
                                        Résumé
                                    </button>
                                </div>
                            </div>

                            <div className="result-content">
                                {(!activeTab[result.id] || activeTab[result.id] === 'extrait') ? (
                                    result.decision_complet ? (
                                        <p
                                            className="result-excerpt"
                                            dangerouslySetInnerHTML={{
                                                __html: highlightKeywords(
                                                    createContextualExcerpt(
                                                        result.decision_complet,
                                                        allKeywords
                                                    ),
                                                    allKeywords
                                                ),
                                            }}
                                        />
                                    ) : null
                                ) : (
                                    <div className="resume-locked">
                                        <Lock size={20} style={{ marginRight: '8px', color: '#F57C00' }} />
                                        <span style={{ color: '#4b5563' }}>
                                            Le résumé est accessible uniquement aux utilisateurs premium.
                                        </span>
                                        <button className="btn-subscribe" onClick={() => setIsPremiumModalOpen(true)}>
                                            Souscrire
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="result-footer">
                                <div className="keywords-container">
                                    <span className="mots-cles-label">Mots-clés:</span>
                                    <div className="result-keywords">
                                        {allKeywords.map((keyword, i) => (
                                            <span key={i} className="keyword-badge">
                                                {keyword}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <Link to={`/decision/${result.id}`} className="result-full-link">
                                    <button className="btn-consult">
                                        Consulter la décision
                                        <ArrowRight size={16} className="icon-arrow" />
                                    </button>
                                </Link>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Pagination
                data={searchData}
                currentPage={parseInt(window.location.search.split('page=')[1]?.split('&')[0] || '1', 10)}
                onPageChange={handlePageChange}
                searchQuery={searchQuery}
            />
        </div>
    );
};

export default Resultsearch;